import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import Resource, { ResourceTypeEnum } from '@/models/_deprecated/Resource';
import { ResourceFields } from '@/data/services/_deprecated/resourceServices';
import { buildChangedObject } from '@/utils/buildChangedObject';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import getKlassViewBaseUrl from '@/utils/getKlassViewBaseUrl';
import ResourceCard from '../ResourceCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

type Props = {
  resource: Resource;
  removeResource(resourceId: number): Promise<void>;
  updateResource(
    resourceId: number,
    body: Partial<ResourceFields>,
  ): Promise<void>;
  loading?: boolean;
  editingAllowed?: boolean;
  klassId?: number;
  lessonId: number;
  slugCourseName?: string;
};

export default function ResourceEditor(props: Props) {
  const {
    resource,
    updateResource,
    removeResource,
    loading,
    editingAllowed,
    lessonId,
    slugCourseName,
    klassId,
  } = props;

  const { id: resourceId, title, isMandatory } = resource;

  const [editing, setEditing] = useState(resource.uploadingProgress === 100);
  const [visible, setVisible] = useState(false);
  const [resourceType, setResourceType] = useState<ResourceTypeEnum>(
    ResourceTypeEnum.NONE,
  );

  const onEditResource = (type: ResourceTypeEnum) => {
    setResourceType(type);
  };

  const { control, setValue } = useForm<ResourceFields>({
    defaultValues: {
      title,
      isMandatory,
    },
  });

  const onSubmit = async (body: Partial<ResourceFields>) => {
    const changedFields = buildChangedObject<Resource>(resource, body);

    if (Object.keys(changedFields).length) {
      try {
        await updateResource(resourceId, changedFields);
      } catch (error) {
        alert.error(getErrorMessage(error));
      } finally {
        onEditResource(ResourceTypeEnum.NONE);
      }
    }
  };

  const url = slugCourseName
    ? `${getKlassViewBaseUrl({
        lessonId,
        slugCourseName,
        klassId: Number(klassId),
      })}/resources/${resource.id}`
    : '';

  return (
    <ResourceCard disabled={resource.uploading}>
      <ResourceCard.ModalRemove
        onConfirm={() => removeResource(resourceId)}
        visible={visible}
        onCancel={() => setVisible(false)}
      />
      <section className="flex gap-2.5 justify-between items-start flex-wrap">
        <section className="flex gap-2.5 flex-wrap items-center">
          <section className="flex gap-2.5 items-center">
            <ResourceCard.Icon resource={resource} />
            <ResourceCard.Title
              control={control}
              editing={editing}
              defaultValue={resource.title}
              loading={resource.uploading}
              onSubmit={onSubmit}
            />
          </section>

          <section className="flex gap-2.5 items-center">
            <ResourceCard.Preview resource={resource} />
            <ResourceCard.ChangeButton
              resource={resource}
              editing={editing}
              onEdit={onEditResource}
            />

            <ResourceCard.ModalEdit
              visible={resourceType !== ResourceTypeEnum.NONE}
              resource={resource}
              resourceType={resourceType}
              onClose={() => onEditResource(ResourceTypeEnum.NONE)}
              onSubmit={onSubmit}
              loading={loading}
            />
          </section>
        </section>
        <section
          className={twMerge('flex gap-2.5', editing ? 'relative top-1.5' : '')}
        >
          <ResourceCard.EditButton
            editing={editing}
            onEdit={() => setEditing(prev => !prev)}
          />
          <ConditionalRenderer condition={url}>
            <ResourceCard.ViewButton uploading={resource.uploading} url={url} />
          </ConditionalRenderer>

          <ResourceCard.RemoveButton
            disabled={!editingAllowed}
            onRemove={() => setVisible(prev => !prev)}
          />
        </section>
      </section>
      <section className="flex justify-between">
        <ResourceCard.Activities
          resource={resource}
          editing={editing}
          updateResource={updateResource}
          loading={loading}
          editingAllowed={editingAllowed}
        />

        <div className="flex divide-x divide-primary-content [&>*]:px-2.5">
          <ResourceCard.OnlyTeacherCheckbox
            id={`${resource.id}`}
            disabled={!editingAllowed}
            checked={resource.onlyTeacher}
            editing={editing}
            onChange={checked => {
              updateResource(resourceId, {
                onlyTeacher: checked,
                isMandatory: checked ? false : resource.isMandatory,
              });
              setValue('onlyTeacher', checked);
            }}
          />

          <ResourceCard.MandatoryCheckbox
            id={`${resource.id}`}
            disabled={!editingAllowed}
            checked={resource.isMandatory}
            editing={editing}
            onChange={checked => {
              updateResource(resourceId, {
                isMandatory: checked,
                onlyTeacher: checked ? false : resource.onlyTeacher,
              });
              setValue('isMandatory', checked);
            }}
          />
        </div>
      </section>
      <ResourceCard.UploadProgress progress={resource.uploadingProgress} />
    </ResourceCard>
  );
}
